import { AssetReport, InvestmentItem, ReportOwnerType } from '../type/AssetReport.type';
import { InvestmentIncome } from '../../investmentIncome/type/InvestmentIncome.type';
import { getAssetInvestmentItemList, getCompareAssetCardData } from './AssetStatusCard.util';

const hasDividendAmount = (item: InvestmentItem) =>
  Object.prototype.hasOwnProperty.call(item, 'dividendAmount');

const getDividendAmount = (report: AssetReport, owner?: ReportOwnerType) =>
  getAssetInvestmentItemList(report.reportItemList)
    .filter((item) => (!owner || item.owner === owner) && hasDividendAmount(item))
    .reduce((sum, item) => sum + (Number(item.dividendAmount) || 0), 0);

const isCoveredByNewDividendInput = (report: AssetReport, income: InvestmentIncome) =>
  report.yearMonth === income.paymentDate.slice(0, 7) &&
  getAssetInvestmentItemList(report.reportItemList).some(
    (item) =>
      item.name === income.assetName &&
      item.owner === income.owner &&
      hasDividendAmount(item),
  );

const getLegacyDividendAmount = (
  reportList: AssetReport[],
  incomeList: InvestmentIncome[],
  yearMonth?: string,
  owner?: ReportOwnerType,
) =>
  incomeList
    .filter(
      (income) =>
        (!yearMonth || income.paymentDate.slice(0, 7) === yearMonth) &&
        (!owner || income.owner === owner) &&
        !reportList.some((report) => isCoveredByNewDividendInput(report, income)),
    )
    .reduce((sum, income) => sum + (Number(income.amount) || 0), 0);

const filterReportByOwner = (report: AssetReport, owner?: ReportOwnerType): AssetReport =>
  owner
    ? { ...report, reportItemList: report.reportItemList.filter((item) => item.owner === owner) }
    : report;

const getEvaluationProfitBetweenReports = (
  currentReport: AssetReport,
  previousReport: AssetReport | undefined,
  owner?: ReportOwnerType,
) => {
  if (!previousReport) return 0;
  return getCompareAssetCardData(
    filterReportByOwner(currentReport, owner),
    filterReportByOwner(previousReport, owner),
  ).investmentChangeProfitAmount;
};

export const getMonthlyInvestmentDividendAmount = (
  report: AssetReport,
  owner?: ReportOwnerType,
  legacyIncomeList: InvestmentIncome[] = [],
) => getDividendAmount(report, owner) + getLegacyDividendAmount([report], legacyIncomeList, report.yearMonth, owner);

export const getYearToMonthInvestmentDividendAmount = (
  reportList: AssetReport[],
  yearMonth: string,
  owner?: ReportOwnerType,
  legacyIncomeList: InvestmentIncome[] = [],
) =>
  reportList
    .filter(
      (report) =>
        report.yearMonth.slice(0, 4) === yearMonth.slice(0, 4) && report.yearMonth <= yearMonth,
    )
    .reduce((sum, report) => sum + getDividendAmount(report, owner), 0) +
  getLegacyDividendAmount(
    reportList,
    legacyIncomeList.filter(
      (income) =>
        income.paymentDate.slice(0, 4) === yearMonth.slice(0, 4) &&
        income.paymentDate.slice(0, 7) <= yearMonth,
    ),
    undefined,
    owner,
  );

export const getTotalInvestmentDividendAmount = (
  reportList: AssetReport[],
  owner?: ReportOwnerType,
  legacyIncomeList: InvestmentIncome[] = [],
) =>
  reportList.reduce((sum, report) => sum + getDividendAmount(report, owner), 0) +
  getLegacyDividendAmount(reportList, legacyIncomeList, undefined, owner);

export const getMonthlyInvestmentPerformanceAmount = (
  currentReport: AssetReport,
  previousReport: AssetReport | undefined,
  owner?: ReportOwnerType,
  legacyIncomeList: InvestmentIncome[] = [],
) =>
  getEvaluationProfitBetweenReports(currentReport, previousReport, owner) +
  getMonthlyInvestmentDividendAmount(currentReport, owner, legacyIncomeList);

export const getYearToMonthInvestmentPerformanceAmount = (
  reportList: AssetReport[],
  yearMonth: string,
  owner?: ReportOwnerType,
  legacyIncomeList: InvestmentIncome[] = [],
) => {
  const targetReports = reportList
    .filter(
      (report) =>
        report.yearMonth.slice(0, 4) === yearMonth.slice(0, 4) && report.yearMonth <= yearMonth,
    )
    .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));

  return targetReports.reduce((sum, report) => {
    const previousReport = reportList
      .filter((candidate) => candidate.yearMonth < report.yearMonth)
      .sort((a, b) => b.yearMonth.localeCompare(a.yearMonth))[0];
    return (
      sum +
      getEvaluationProfitBetweenReports(report, previousReport, owner) +
      getMonthlyInvestmentDividendAmount(report, owner, legacyIncomeList)
    );
  }, 0);
};

export const getTotalInvestmentPerformanceAmount = (
  currentReport: AssetReport,
  reportList: AssetReport[],
  owner?: ReportOwnerType,
  legacyIncomeList: InvestmentIncome[] = [],
) => {
  const targetReports = reportList
    .filter((report) => report.yearMonth <= currentReport.yearMonth)
    .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));

  return targetReports.reduce((sum, report, index) => {
    const previousReport = index > 0 ? targetReports[index - 1] : undefined;
    return (
      sum +
      getEvaluationProfitBetweenReports(report, previousReport, owner) +
      getMonthlyInvestmentDividendAmount(report, owner, legacyIncomeList)
    );
  }, 0);
};
